<template>
  <div
    data-component-group="auth-form"
    data-component="login-form"
    :class="generateTestClass('form')"
  >
    <Form :title="$t('views_login_title')" @submit="submit">
      <span
       class="dont-have-acc"
      >
        {{ $t('dont_have_acc') }}

        <router-link
          to="/register/"
          small
          :ripple="false"
          class="go-to-reset-pass"
        >
          {{ $t('sign_up') }}
        </router-link>
      </span>

      <v-text-field
        v-model="email"
        :class="generateTestClass('form-field-email')"
        prepend-inner-icon="mail"
        :label="$t('email_placeholder')"
        required
        type="email"
        :autofocus="true"
        :error-messages="emailErrors"
        @blur="v$.email.$touch()"
      />

      <v-text-field
        v-model="password"
        :class="generateTestClass('form-field-password')"
        prepend-inner-icon="lock"
        :label="$t('components_forms_login_password_placeholder')"
        required
        :type="isPasswordVisible ? 'text' : 'password'"
        :error-messages="passErrors"
        @blur="v$.password.$touch()"
        @click:append="isPasswordVisible = !isPasswordVisible"
      >
        <template #append-inner>
          <v-btn
            :icon="isPasswordVisible ? 'visibility_off' : 'visibility'"
            density="comfortable"
            @click="isPasswordVisible = !isPasswordVisible"
          />
        </template>
      </v-text-field>

      <div class="form-actions">
        <router-link
          to="/password/reset"
          small
          :ripple="false"
          class="go-to-reset-pass"
        >
          {{ $t('components_forms_login_goto_reset') }}
        </router-link>
        <v-btn
          color="primary"
          :class="generateTestClass('form-btn-submit')"
          :loading="isLoading"
          :disabled="isDisabled"
          type="submit"
        >
          <v-icon size="24" class="mr-3">logout</v-icon>
          {{ $t('components_forms_login_submit') }}
        </v-btn>
      </div>
    </Form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required, email } from '@vuelidate/validators';
  import testClassMixin from '../../../mixins/testClass';
  import recaptchaMixin from '../../../mixins/recaptcha';
  import requestIdleCallback from '@/utils/requestIdleCallback';
  import Form from '../Form';

  export default {
    components: {
      Form,
    },
    setup () {
      return { v$: useVuelidate() }
    },
    mixins: [testClassMixin, recaptchaMixin],
    validations: {
      email: {
        required,
        email,
      },
      password: {
        required,
      },
    },
    props: {
      hasRecaptcha: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      email: '',
      password: '',
      isPasswordVisible: false,
      isSubmitted: false,
      isLoading: false,
    }),
    computed: {
      emailErrors() {
        return this.v$.email.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('email_required');
          }
          if (error.$validator === 'email') {
            return this.$t('email_valid');
          }
          return error.$message;
        });
      },
      passErrors() {
        return this.v$.password.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('components_forms_login_errors_password_required');
          }
          return error.$message;
        });
      },
      isDisabled() {
        const { email, password } = this;

        return email === '' || password === '';
      },
    },
    created() {
      this.$store.watch(
        () => {
          const hasErrors = this.$store.getters['alerts/error'].length > 0;

          if (this.isSubmitted && hasErrors) {
            this.isSubmitted = false;
            this.isLoading = false;
          }
        },
        () => {},
      );
    },
    methods: {
      async submit() {
        if (this.isLoading) return;

        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        let token = '';
        if (this.hasRecaptcha) {
          token = await this.getRecaptchaToken('login');

          // reCAPTCHA is unavailable and the user has been alerted: submitting
          // without a token would only fail server-side.
          if (token === null) {
            return false;
          }
        }
        return this.sendLoginRequest(token);
      },
      clear() {
        this.v$.$reset();
        this.email = '';
        this.password = '';
        this.isSubmitted = false;
      },
      sendLoginRequest(token = '') {
        const userAuth = {
          email: this.email.trim(),
          password: this.password,
        };

        if (this.hasRecaptcha) {
          userAuth['g-recaptcha-response'] = token;
        }

        this.isSubmitted = true;
        this.isLoading = true;
        return this.$store
          .dispatch('auth/login', userAuth)
          .then((res) => {
            requestIdleCallback(() => {
              import('../../../views/threads/Index.vue');
              import('../../../views/threads/AddDeal.vue');
              import('../../../views/threads/AddCampaign.vue');
              import('../../../views/threads/EditThread.vue');
              import('../../../views/threads/AddComment.vue');
            });
            return res;
          })
          .catch((err) => err)
          .finally(() => {
            this.isLoading = false;
          });
      },
      canSubmitRequest() {
        return this.email !== '' && this.password !== '' && !this.isSubmitted;
      },
    },
  };
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/theme' as *;

[data-component-group='auth-form'] {
  &[data-component='login-form'] {
    width: 40%;

    [data-component='form'] {
      width: 44%;
      min-width: 330px;
      margin: 0 auto;
      padding: 29.5vh 0 100px;
    }
  }
}

[data-component-group='auth-form'][data-component='login-form'] {
  @include auth-form-narrow;
}
</style>
